/**
 * Workspace models
 */

export interface WorkspaceDefinition {
  id: string;
  definitionId?: string; // Family ID across versions
  name: string;
  description?: string;
  isPublic: boolean;
  tenantId: string; // Required - all definitions belong to a tenant
  createdBy: { id: string; email: string };
  createdAt: Date;
  version?: number; // Assigned on publish
  status?: WorkspaceDefinitionStatus;
  publishedAt?: Date;
  isAvailable: boolean;
  deletedAt?: Date;
  sessionAccessMatrix?: SessionAccessMatrix;
  policies?: WorkspacePolicies;
  featureToggles: FeatureToggles;
  restrictions: Restriction[];
  visibility: VisibilityRules;
  configurableOptions: ConfigurableOption[];
  queueDefinitions: QueueDefinition[];
  formDefinitions: FormDefinition[];
  boardDefinitions: BoardDefinition[];
  routerDefinitions?: RouterDefinition[];
  queueRules?: QueueRules;
  structureControls: StructureControls;
  sessionBehaviorControls: SessionBehaviorControls;
  thumbnailUrl?: string;
}

export type WorkspaceDefinitionStatus = 'Draft' | 'Published' | 'Superseded';

export interface FeatureToggles {
  formsEnabled: boolean;
  boardsEnabled: boolean;
  queuesEnabled: boolean;
  [key: string]: boolean;
}

export interface Restriction {
  type: string;
  value: any;
  [key: string]: any;
}

export interface VisibilityRules {
  allowedUserGroups: string[];
  allowedRoles: string[];
  isPublic: boolean;
}

export interface ConfigurableOption {
  key: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'multiselect' | 'range';
  defaultValue: any;
  allowedValues?: {
    min?: number;
    max?: number;
    options?: any[];
  };
  required: boolean;
  label: string;
  description?: string;
  validationRules?: any;
}

export interface WorkspaceInstance {
  id: string;
  workspaceDefinitionId: string;
  workspaceDefinitionVersion?: number;
  workspaceDefinitionVersionId?: string;
  tenantId: string;
  name: string;
  description?: string;
  createdBy: { id: string; email: string };
  createdAt: Date;
  status: string;
  customizationOptions: Record<string, any>;
  sessionAccessOverrides?: SessionAccessMatrix;
  /** Workspace-scoped Canvas LMS API key used by integrations (e.g. student ID validation). */
  canvasLmsApiKey?: string;
  queueInstances: QueueInstance[];
  formInstances: FormInstance[];
  boardInstances: BoardInstance[];
  routerInstances?: RouterInstance[];
  allowCustomQueues?: boolean; // Workspace-level toggle when extensibility is WORKSPACE_DECIDES
  thumbnailUrl?: string;
}

export interface WorkspaceBookmark {
  id: string;
  workspaceInstanceId: string;
  workspaceName: string;
  workspaceStatus: string;
  workspaceCreatedByEmail?: string;
  createdAt: Date;
}

export interface WorkspacePolicies {
  allowReassignment: {
    enabled: boolean;
    afterCompletion: boolean;
  };
  allowPriorityChange: {
    enabled: boolean;
    afterStart: boolean;
  };
  preserveTimestampOnMove: boolean;
  autoEscalateUnserved: {
    enabled: boolean;
    afterMinutes: number;
  };
  allowRuntimeFormControl: boolean;
  allowRuntimeBoardControl: boolean;
}

export const defaultWorkspacePolicies: WorkspacePolicies = {
  allowReassignment: {
    enabled: true,
    afterCompletion: false,
  },
  allowPriorityChange: {
    enabled: true,
    afterStart: false,
  },
  preserveTimestampOnMove: true,
  autoEscalateUnserved: {
    enabled: true,
    afterMinutes: 15,
  },
  allowRuntimeFormControl: true,
  allowRuntimeBoardControl: true,
};

export type SessionAccessRole = 'coordinator' | 'responder' | 'observer';

export type SessionAccessPermission =
  | 'viewSession'
  | 'takeWork'
  | 'completeWork'
  | 'reassignWork'
  | 'changePriority'
  | 'controlIntake'
  | 'controlBoards';

export type SessionAccessMatrix = Record<
  SessionAccessRole,
  Record<SessionAccessPermission, boolean>
>;

export const defaultSessionAccessMatrix: SessionAccessMatrix = {
  coordinator: {
    viewSession: true,
    takeWork: true,
    completeWork: true,
    reassignWork: true,
    changePriority: true,
    controlIntake: true,
    controlBoards: true,
  },
  responder: {
    viewSession: true,
    takeWork: true,
    completeWork: true,
    reassignWork: false,
    changePriority: false,
    controlIntake: false,
    controlBoards: false,
  },
  observer: {
    viewSession: true,
    takeWork: false,
    completeWork: false,
    reassignWork: false,
    changePriority: false,
    controlIntake: false,
    controlBoards: false,
  },
};

export interface TimestampUser {
  at: Date | string; // ISO string from API, converted to Date in frontend
  by: string | { id: string; email: string }; // Can be string (legacy) or enriched object
}

export interface Session {
  id: string;
  workspaceInstanceId: string;
  name: string;
  workspace?: {
    id: string;
    name: string;
  };
  created?: TimestampUser; // Optional to handle edge cases, but should always be present
  started?: TimestampUser;
  paused?: TimestampUser;
  resumed?: TimestampUser;
  ended?: TimestampUser;
  updatedAt?: Date | string; // ISO string from API, converted to Date in frontend
  status: SessionState;
  queues: SessionQueue[];
  sessionForms: SessionForm[];
  sessionBoards: SessionBoard[];
  sessionRouters?: SessionRouter[];
  servingPolicyId?: string;
  thumbnailUrl?: string;
}

export enum SessionState {
  Draft = 'Draft',
  Open = 'Open',
  Paused = 'Paused',
  Closed = 'Closed',
}

export interface QueueDefinition {
  id: string;
  name: string;
  description?: string;
  alwaysIncluded?: boolean;
  defaultSelected?: boolean;
  capacity?: number;
  settings?: any;
}

export interface QueueInstance {
  id: string;
  queueDefinitionId: string;
  name: string;
  description?: string;
  capacity?: number;
  settings?: any;
  orderCounts?: Record<string, number>; // Order counts by status (e.g., { "PENDING": 5, "IN_PROGRESS": 2 })
}

export interface Queue {
  id: string;
  queueInstanceId?: string; // Optional - null for custom queues
  name: string;
  description?: string;
  capacity?: number;
  settings?: any;
  isCustom: boolean; // True for custom queues created at runtime
  queueTemplateId?: string; // For lineage tracking (optional)
  status?: 'Open' | 'Closed' | 'Full'; // Queue status
}

export interface FormDefinition {
  id: string;
  name: string;
  description?: string;
  alwaysIncluded?: boolean;
  defaultSelected?: boolean;
  fields: FormField[];
  settings?: FormSettings;
}

export interface FormInstance {
  id: string;
  formDefinitionId: string;
  name: string;
  description?: string;
  fields: FormField[];
  settings?: FormSettings;
  enabled?: boolean; // New field, defaults to false if not present
  version?: number; // Optimistic locking version, defaults to 0
  isSpecial?: boolean; // True for predefined special forms
  formType?: 'queueSelection' | 'standard'; // Type of special form
}

export interface FormSettings {
  printTicket?: {
    includeQr?: boolean;
    printTicketShowNumberLabel?: boolean;
    printTicketNumberLabel?: string;
    printTicketShowQrCaption?: boolean;
    printTicketQrCaption?: string;
    printTicketShowQueuePrefix?: boolean;
    printTicketQueuePrefix?: string;
  };
  queueSelection?: {
    displayMode?: QueueSelectionDisplayMode;
  };
  [key: string]: any;
}

export type QueueSelectionDisplayMode = 'auto' | 'tiles' | 'radio' | 'select';

export interface SessionForm {
  id: string;
  formInstanceId: string;
  name: string;
  isVisible: boolean;
  fields: FormField[];
  settings?: FormSettings;
  enabled?: boolean; // New field, defaults to false if not present
  version?: number; // Optimistic locking version, defaults to 0
  isSpecial?: boolean; // True for predefined special forms
  formType?: 'queueSelection' | 'standard'; // Type of special form
  inputMode?: 'alphabetic' | 'numeric'; // Input mode for special forms, defaults to 'alphabetic'
  defaultSelectedQueueId?: string | null; // Optional default queue control id for queue-selection forms
}

export interface FormField {
  id: string;
  type: string;
  label: string;
  required: boolean;
  validation?: any;
  options?: any[];
  [key: string]: any;
}

export interface BoardDefinition {
  id: string;
  name: string;
  description?: string;
  alwaysIncluded?: boolean;
  defaultSelected?: boolean;
  type: 'scrum' | 'kanban' | 'custom';
  configuration: any;
  settings?: any;
}

export interface BoardInstance {
  id: string;
  boardDefinitionId: string | null; // null for special boards
  name: string;
  description?: string;
  type: 'scrum' | 'kanban' | 'custom';
  configuration: any;
  settings?: any;
  isSpecial?: boolean;
  boardType?: 'queueStatus' | 'swimlaneOrders' | 'standard';
  /** Swimlane waiting lane layout, when the board is a swimlane board. */
  waitingLayoutMode?: 'flat' | 'queues';
  /**
   * Per-board default for order callout audio (chime + TTS) on swimlane boards.
   * Defaults to true when not set. Per-device localStorage override on the
   * boards app can override this default at the device level.
   */
  audioEnabled?: boolean;
  enabled?: boolean;
  version?: number;
}

export interface SessionBoard {
  id: string;
  boardInstanceId: string;
  name: string;
  isVisible: boolean;
  type: 'scrum' | 'kanban' | 'custom';
  configuration: any;
  settings?: any;
  isSpecial?: boolean;
  boardType?: 'queueStatus' | 'swimlaneOrders' | 'standard';
  /** Swimlane waiting lane layout, when the board is a swimlane board. */
  waitingLayoutMode?: 'flat' | 'queues';
  /**
   * Per-board default for order callout audio (chime + TTS) on swimlane boards.
   * Defaults to true when not set. Per-device localStorage override on the
   * boards app can override this default at the device level.
   */
  audioEnabled?: boolean;
  enabled?: boolean; // Controls whether board is enabled for this session, defaults to false
  version?: number; // Optimistic locking version, defaults to 0
  /**
   * Queue control IDs hidden on this board (queue status boards only).
   * Omitted or empty: all session queues may be shown (subject to queue data).
   */
  hiddenQueueIds?: string[];
}

export interface SessionQueue {
  id: string;
  queueInstanceId: string | null; // Reference to workspace queue instance for traceability
  name: string;
  description?: string;
  capacity?: number;
  settings?: any;
  enabled?: boolean; // Controls whether queue is enabled for this session, defaults to false
  version?: number; // Optimistic locking version, defaults to 0
  status?: 'Open' | 'Closed' | 'Full'; // Queue status
  isCustom?: boolean;
  queueTemplateId?: string | null;
  sessionId?: string;
  workspaceInstanceId?: string;
}

/**
 * Single GET payload for worklist settings (agent). User-specific prefs will be keyed by
 * tenant user / session member on the request; add fields here as the screen grows.
 * Queue list stays aligned with {@link SessionQueue}.
 */
export interface WorklistSettings {
  queues: SessionQueue[];
  retrievedAt: string;
}

export type SessionAlertScope = 'session' | 'queue';

export type SessionAlertMetric =
  | 'createdOrderCount'
  | 'pendingOrderCount'
  | 'inProgressOrderCount'
  | 'completedOrderCount'
  | 'cancelledOrderCount'
  | 'minimumWaitTimeMinutes'
  | 'averageWaitTimeMinutes'
  | 'medianWaitTimeMinutes'
  | 'maximumWaitTimeMinutes'
  | 'minimumServiceTimeMinutes'
  | 'averageServiceTimeMinutes'
  | 'medianServiceTimeMinutes'
  | 'maximumServiceTimeMinutes'
  | 'minimumTurnaroundTimeMinutes'
  | 'averageTurnaroundTimeMinutes'
  | 'medianTurnaroundTimeMinutes'
  | 'maximumTurnaroundTimeMinutes';

export type SessionAlertComparator = 'lt' | 'lte' | 'eq' | 'neq' | 'gte' | 'gt';

/** Session-level timeline events (join / leave service from My Worklist presence). */
export type SessionActivityType = 'SERVICE_JOINED' | 'SERVICE_LEFT' | 'SERVING_STARTED';

/**
 * One session activity row (API shape aligned with order activity items where practical).
 */
export interface SessionActivityItem {
  id: string;
  type: SessionActivityType;
  occurredAt: string;
  description: string;
  /**
   * Who performed the action; `displayName` is set by the API from the tenant user profile.
   */
  actor?: import('./order-actor-ref.model').OrderActorRef | null;
}

export interface SessionAlert {
  id: string;
  name: string;
  /**
   * When false, the alert is inactive (not evaluated). Omitted or true = enabled.
   */
  enabled?: boolean;
  /** Human-readable summary (e.g. "Queue A: Pending orders ≥ 5"). Generated by API. */
  summary?: string;
  scope: SessionAlertScope;
  /** @deprecated Use queueIds for multiple queues. Single queue for backward compat. */
  queueId?: string | null;
  /** Queue IDs when scope=queue and specific queues selected. Empty/missing = any queue. */
  queueIds?: string[];
  metric: SessionAlertMetric;
  comparator: SessionAlertComparator;
  value: number;
  createdAt?: Date;
  description?: string | null;
}

export interface RouterDefinition {
  id: string;
  name: string;
  description?: string;
  sourceType: SourceType;
  sourceId?: string;
  sourcePattern?: string;
  priority: number;
  routes: RouteDefinition[];
  fallbackQueueId?: string;
  enabled: boolean;
}

export interface RouterInstance {
  id: string;
  routerDefinitionId?: string;
  name: string;
  description?: string;
  level: RouterLevel;
  sourceType: SourceType;
  sourceId?: string;
  sourcePattern?: string;
  priority: number;
  routes: RouteInstance[];
  fallbackQueueId?: string;
  enabled: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export interface SessionRouter {
  id: string;
  routerInstanceId?: string;
  name: string;
  description?: string;
  sourceType: SourceType;
  sourceId?: string;
  sourcePattern?: string;
  priority: number;
  routes: RouteInstance[];
  fallbackQueueId?: string;
  enabled: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface RouteDefinition {
  id: string;
  routerId: string;
  name: string;
  description?: string;
  priority: number;
  conditions: RouteCondition[];
  targetQueueId: string;
  enabled: boolean;
}

export interface RouteInstance {
  id: string;
  routerId: string;
  name: string;
  description?: string;
  priority: number;
  conditions: RouteCondition[];
  targetQueueId: string;
  enabled: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface RouteCondition {
  field: string;
  operator: '==' | '!=' | '>' | '<' | '>=' | '<=' | 'contains' | 'matches';
  value: any;
}

export enum SourceType {
  FORM = 'FORM',
  IOT_DEVICE = 'IOT_DEVICE',
  TEAMS = 'TEAMS',
  SMS = 'SMS',
  EMAIL = 'EMAIL',
  API = 'API',
  MANUAL = 'MANUAL',
  ALL = 'ALL',
}

export enum RouterLevel {
  DEFINITION = 'DEFINITION',
  INSTANCE = 'INSTANCE',
  SESSION = 'SESSION',
}

export type QueueExtensibility = 'FORBIDDEN' | 'WORKSPACE_DECIDES' | 'ALLOWED';

export interface QueueTemplate {
  defaultPriority?: number;
  agingRate?: number;
  visibilityFlags?: any;
  slaHints?: any;
}

export interface QueueRules {
  extensibility: QueueExtensibility;
  maxCustomQueues?: number;
  allowedRoles: string[]; // e.g., ['Coordinator', 'Manager']
  queueTemplate?: QueueTemplate;
}

export interface StructureControls {
  // Queue controls
  allowAdditionalQueues: boolean;
  maxQueueCount?: number;
  lockPredefinedQueues: boolean;
  allowQueueRemoval: boolean;
  allowQueueReordering: boolean;

  // Form controls
  allowAdditionalForms: boolean;
  maxFormCount?: number;
  lockPredefinedForms: boolean;
  allowFormRemoval: boolean;

  // Board controls
  allowAdditionalBoards: boolean;
  maxBoardCount?: number;
  lockPredefinedBoards: boolean;
  allowBoardRemoval: boolean;
  allowBoardReordering: boolean;
}

export interface ResponderActionRestrictions {
  canMoveItems: boolean;
  canUpdateBoards: boolean;
  canChangePriorities: boolean;
  canReassignOrders: boolean;
  // Extensible - add more as needed
  [key: string]: boolean;
}

export interface SessionBehaviorControls {
  allowSessionTimeControl: boolean; // Open/pause/close structures during sessions
  restrictResponderActions?: ResponderActionRestrictions;
  allowObserversToJoinSessions: boolean;
}
